import { useNavigate } from 'react-router-dom';
import { salesApi, type SalesDashboardTrip } from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { salesNavItems } from '@/pages/navItems';
import { formatPeso as money } from '@/lib/format';

export function SalesDashboard() {
  const navigate = useNavigate();
  const { data: dashboard, loading, error, reload } = useAsync(() => salesApi.dashboard(), []);

  // Show a placeholder in stat cards while the first load is in flight, instead of "₱0" / "—".
  const stat = (value: string | number) => (loading && !dashboard ? '…' : value);
  const perTrip = dashboard?.visibility === 'per_trip_cost_gp';
  const prospectsAsClients = (dashboard?.clients ?? []).length;

  const tripColumns = perTrip
    ? [
        { key: 'tripNumber', header: 'Trip' },
        { key: 'clientName', header: 'Client' },
        {
          key: 'status',
          header: 'Status',
          render: (row: SalesDashboardTrip) => <StatusBadge status={row.status} size="sm" />,
        },
        {
          key: 'tripRevenue',
          header: 'Revenue',
          render: (row: SalesDashboardTrip) => money(row.tripRevenue ?? row.quotedAmount),
        },
        {
          key: 'allocatedCost',
          header: 'Cost',
          render: (row: SalesDashboardTrip) => money(row.allocatedCost),
        },
        {
          key: 'grossProfit',
          header: 'Gross profit',
          render: (row: SalesDashboardTrip) => money(row.grossProfit),
        },
        {
          key: 'commissionAmount',
          header: 'Commission',
          render: (row: SalesDashboardTrip) => money(row.commissionAmount),
        },
      ]
    : [
        { key: 'tripNumber', header: 'Trip' },
        { key: 'clientName', header: 'Client' },
        {
          key: 'status',
          header: 'Status',
          render: (row: SalesDashboardTrip) => <StatusBadge status={row.status} size="sm" />,
        },
        {
          key: 'quotedAmount',
          header: 'Selling price',
          render: (row: SalesDashboardTrip) => money(row.quotedAmount),
        },
      ];

  return (
    <Layout navItems={salesNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <h1>Sales dashboard</h1>
            <p className="text-muted">Your accounts, trips and commission</p>
          </div>
          <div className="row" style={{ flexWrap: 'wrap' }}>
            <Button variant="secondary" type="button" onClick={() => navigate('/app/sales/settlements')}>
              Settlements
            </Button>
            <Button type="button" onClick={() => navigate('/app/sales/submit')}>Submit account</Button>
          </div>
        </div>

        {error && (
          <div className="sales-load-error" role="alert">
            <span>Your dashboard couldn&apos;t be loaded. {error}</span>
            <Button variant="secondary" size="sm" type="button" onClick={() => reload()}>Retry</Button>
          </div>
        )}

        <div className="grid-3" style={{ marginBottom: 'var(--space-xl)' }}>
          <div className="stat-block">
            <span className="stat-value">{stat(dashboard?.clientCount ?? prospectsAsClients)}</span>
            <span className="stat-label">Your accounts</span>
          </div>
          <div className="stat-block">
            <span className="stat-value">{stat(money(dashboard?.commissionEarned))}</span>
            <span className="stat-label">Commission earned</span>
          </div>
          <div className="stat-block">
            <span className="stat-value">
              {stat(
                money(
                  perTrip
                    ? dashboard?.grossProfitAggregate
                    : dashboard?.grossProfitEarned ?? dashboard?.grossProfitAggregate,
                ),
              )}
            </span>
            <span className="stat-label">
              {perTrip ? 'Gross profit (all trips)' : 'Gross profit (total)'}
            </span>
          </div>
        </div>

        <div className="grid-3" style={{ marginBottom: 'var(--space-xl)' }}>
          <div className="stat-block">
            <span className="stat-value">{stat(money(dashboard?.commissionProvisional))}</span>
            <span className="stat-label">Commission pending (not yet settled)</span>
          </div>
          <div className="stat-block">
            <span className="stat-value">{stat(money(dashboard?.grossProfitProvisional))}</span>
            <span className="stat-label">Gross profit pending</span>
          </div>
          <div className="stat-block">
            <span className="stat-value">{stat(dashboard?.recentTrips.length ?? 0)}</span>
            <span className="stat-label">Recent trips on your accounts</span>
          </div>
        </div>

        <div className="row-between" style={{ marginBottom: 'var(--space-md)' }}>
          <h3>{perTrip ? 'Recent trips — cost and gross profit' : 'Recent trips on your accounts'}</h3>
          <Button variant="secondary" size="sm" type="button" onClick={() => navigate('/app/sales/prospects')}>
            Prospects
          </Button>
        </div>
        <DataTable
          columns={tripColumns}
          data={dashboard?.recentTrips ?? []}
          keyField="id"
          loading={loading}
          loadError={error}
          dense
          emptyMessage="No trips on your accounts yet. Submit an account to start building your book."
        />

        <style>{`
          .stat-block {
            background: var(--color-surface);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            padding: var(--space-lg);
            text-align: center;
          }
          .stat-value {
            display: block;
            font-family: var(--font-display);
            font-size: 1.5rem;
            font-weight: 700;
            color: var(--color-forest);
          }
          .sales-load-error {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: var(--space-md);
            flex-wrap: wrap;
            background: #FDE8E8;
            color: var(--color-urgent);
            padding: var(--space-sm) var(--space-md);
            border-radius: var(--radius-sm);
            margin-bottom: var(--space-lg);
            font-size: 0.875rem;
          }
          .stat-label {
            font-size: 0.8125rem;
            color: var(--color-text-muted);
          }
        `}</style>
      </div>
    </Layout>
  );
}
